import { mapGetters } from 'vuex';

export default {
    name: 'advantage',
    computed: {
        ...mapGetters('glb', ['glb', 'getStateData', 'getSplit']),
        ...mapGetters('msp', ['msp', 'geo', 'getNearbyCities', 'getNearbyCounties', 'getNearbyZipcodes', 'getNearbyNeighborhood', 'getColloquialAreaInfo', 'getGeoDisplayText']),
        /* Booleans */
        showAdvantage() {
            return ['CITY', 'ZIPCODE', 'NEIGHBORHOOD'].includes(this.geoType) && ((!this.isListingObjEmpty && !this.isSeoInfoZero) || this.overrideInventoryCount);
        },
        isStateSearch() {
            return this.geoType === 'STATE';
        },
        shouldShowAttributeLinks() {
            return !['ZIPCODE', 'NEIGHBORHOOD'].includes(this.geoType);
        },
        isListingObjEmpty() {
            return this.glb.listingCountObj.constructor === Object && Object.keys(this.glb.listingCountObj).length === 0;
        },
        isSeoInfoZero() {
            return Object.entries(this.getSeoInfo)
                .filter(([key]) => key.endsWith('Count'))
                .every(([, value]) => value === 0);
        },
        overrideInventoryCount() {
            return this.isListingObjEmpty && this.msp.isUnfilteredUrl && this.msp.totalCount >= 10;
        },
        /* Formatting */
        geoType() {
            return this.geo.geoType;
        },
        stateFullName() {
            return this.getStateData[this.geo.state] || this.geo.state;
        },
        summaryText() {
            const variant = this.msp.contentVariant.isUnfilteredUnpaginatedCity ? 2 : 1;
            return this.molang(`search.advantage.movoto${variant}`, {
                filterName: this.getSeoInfo.filterName,
                nearbyCity: this.getSeoInfo.nearbyZipOrCityOrNeighbor,
                stateFullName: this.stateFullName,
            });
        },
        /* URL Generation */
        limitFilters() {
            const basicFilters = ['forSale', 'singleFamily', 'condo', 'multiFamily', 'land', 'mobile'];
            const extra = [];
            if (!this.msp.filter.updateNew) extra.push('new');
            if (!this.msp.filter.updateNewConstruction) extra.push('newConstruction');
            return [...basicFilters, ...extra, 'rentals'];
        },
        getSeoInfo() {
            const { geo, msp, glb, geoType, limitFilters } = this;
            const { filter } = msp;
            const seoInfo = {};

            const searchParams = {
                type: ['ZIPCODE', 'NEIGHBORHOOD'].includes(geoType) ? msp.searchType : 'city',
                cityName: geo.city,
                stateCode: geo.stateCode,
                zipcode: geo.zipcode,
                neighborhood: geo.neighborhoodCode,
                filterArray: ['attributes', ...limitFilters],
                listingCountObj: glb.listingCountObj,
                rentals: false,
            };

            const { filteredUrlObj = {}, sold, base, baseCount } = this.generateMapSearchUrl(glb.appUrl, searchParams);

            if (filteredUrlObj.attributes) {
                filteredUrlObj.attributes = filteredUrlObj.attributes.replace(/\/$/, '');
            }

            Object.assign(seoInfo, filteredUrlObj, {
                stateName: geo.stateName,
                sold,
                base,
                baseCount,
                filterName: this.getFilterName(filter),
                ...this.getNearbyAreas(geoType),
            });

            return seoInfo;
        },
        attributeLinks() {
            const out = {};
            for (const key in this.msp.attrSSRLinks) {
                if (!this.msp.filter.ojoAttribute.includes(key)) {
                    out[key] = this.msp.attrSSRLinks[key];
                }
            }
            return out;
        },
        seoLinkItems() {
            const nearby = this.getSeoInfo.nearbyZipOrCityOrNeighbor;
            const links = [];

            for (const item of this.limitFilters) {
                if (item === 'forSale') {
                    if ((this.getSeoInfo.base && this.getSeoInfo.baseCount) || this.overrideInventoryCount) {
                        links.push({
                            href: this.getSeoInfo.base,
                            text: `${nearby} ${this.molang('search.common.homesForSale')}`,
                        });
                    }
                } else {
                    const countKey = `${item}Count`;
                    if (this.getSeoInfo[countKey] || this.overrideInventoryCount) {
                        links.push({
                            href: this.getSeoInfo[item],
                            text: `${nearby} ${this.molang(`search.advantage.${item.split('-')[0]}`, { count: item.split('-')[1] })}`,
                        });
                    }
                }
            }

            if (((this.getSeoInfo.base && this.getSeoInfo.baseCount >= 10) || this.overrideInventoryCount) && !['ZIPCODE', 'NEIGHBORHOOD'].includes(this.geoType)) {
                links.push({
                    href: this.getSeoInfo.rentals,
                    text: `${nearby} ${this.molang('search.advantage.rentals')}`,
                });

                links.push({
                    href: this.getSeoInfo.rentals + 'condos/',
                    text: `${nearby} ${this.molang('search.advantage.apartmentsRent')}`,
                });

                links.push({
                    href: this.getSeoInfo.rentals + 'single-family/',
                    text: `${nearby} ${this.molang('search.advantage.singleFamilyRent')}`,
                });
            }

            if (!this.msp.filter.soldDateRange) {
                links.push({
                    href: this.getSeoInfo.sold,
                    text: `${nearby} ${this.molang('search.advantage.sold')}`,
                });
            }

            if (this.shouldShowAttributeLinks) {
                for (const [key, path] of Object.entries(this.attributeLinks)) {
                    links.push({
                        href: `${this.glb.appUrl}${path}/`,
                        text: `${nearby} ${this.molang(`search.advantage.${key}`)}`,
                    });
                }
            }

            return links;
        },
    },
    methods: {
        getFilterName(filter) {
            if (!filter) return 'real estate';

            if (filter.propertyCriteriaOpenHouse) {
                return 'open houses, live stream and virtual 3D tour';
            }

            if (filter.propertyType?.length) {
                return filter.propertyType[0].toLowerCase();
            }

            return 'real estate';
        },
        getNearbyAreas(geoType) {
            const { geo, glb } = this;
            const getDisplay = (area) => area?.displayName || '';
            const getUrl = (area, key) => (area ? glb.appUrl + area[key] : '');

            const [city1, city2] = this.getNearbyCities;
            const [county1] = this.getNearbyCounties;

            switch (geoType) {
                case 'ZIPCODE':
                    return {
                        nearbyZipOrCityOrNeighbor: geo.zipcode,
                        otherArea1: getDisplay(city1),
                        otherArea1Link: getUrl(city1, 'sitemapCityPropertyPageUrl'),
                        otherArea2: getDisplay(county1),
                        otherArea2Link: getUrl(county1, 'sitemapCountyPageUrl'),
                    };
                case 'NEIGHBORHOOD':
                    return {
                        nearbyZipOrCityOrNeighbor: geo.neighborhood,
                        otherArea1: getDisplay(city1),
                        otherArea1Link: getUrl(city1, 'sitemapCityPropertyPageUrl'),
                        otherArea2: getDisplay(county1),
                        otherArea2Link: getUrl(county1, 'sitemapCountyPageUrl'),
                    };
                case 'CITY':
                    return {
                        nearbyZipOrCityOrNeighbor: geo.city,
                        otherArea1: getDisplay(city1),
                        otherArea1Link: getUrl(city1, 'sitemapCityPropertyPageUrl'),
                        otherArea2: getDisplay(city2),
                        otherArea2Link: getUrl(city2, 'sitemapCityPropertyPageUrl'),
                    };
                case 'STATE':
                    return {
                        nearbyZipOrCityOrNeighbor: geo.state,
                    };
                default:
                    return {};
            }
        },
    },
};
